import { format, parse } from "date-fns";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendar } from "@fortawesome/pro-solid-svg-icons/faCalendar";
import {
  Button,
  Calendar,
  Popover,
  PopoverContent,
  PopoverTrigger,
  cn,
} from "@wondermaps/ui";

interface DatePickerFieldProps {
  value: string; // YYYY-MM-DD or empty
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  /** Show a "Clear" button when a date is selected */
  clearable?: boolean;
  /** Label shown above the button (used by AuditLogFilters) */
  label?: string;
}

export function DatePickerField({
  value,
  onChange,
  placeholder,
  className,
  clearable = false,
  label,
}: DatePickerFieldProps) {
  const dateValue = value ? parse(value, "yyyy-MM-dd", new Date()) : undefined;

  const picker = (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "w-full justify-start text-left font-normal",
            !value && "text-muted-foreground",
            className,
          )}
        >
          <FontAwesomeIcon icon={faCalendar} className="mr-2 h-4 w-4 shrink-0" />
          {value && dateValue ? format(dateValue, "PPP") : placeholder || "Pick a date"}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={dateValue}
          onSelect={(date) => {
            onChange(date ? format(date, "yyyy-MM-dd") : "");
          }}
          initialFocus
        />
        {clearable && value && (
          <div className="p-2 border-t">
            <Button
              variant="ghost"
              size="sm"
              className="w-full"
              onClick={() => onChange("")}
            >
              Clear
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );

  if (label) {
    return (
      <div className="space-y-1 flex flex-col">
        <label className="text-xs text-muted-foreground">{label}</label>
        {picker}
      </div>
    );
  }

  return picker;
}
